import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  MapPin, Award, Target, DollarSign, Save, AlertCircle, Gift, Zap, 
  Code, Link as LinkIcon, Eye, Copy, ExternalLink, Upload, Loader
} from 'lucide-react';

const AdminBadgeEditor = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { badgeId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allBadges, setAllBadges] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    icon_url: '🏅',
    badge_type: 'location',
    target_latitude: '',
    target_longitude: '',
    geofence_radius_meters: 50,
    venue_name: '',
    venue_category: '',
    hint_text: '',
    required_posts_count: '',
    required_login_days: '',
    required_comments_count: '',
    required_votes_count: '',
    max_supply: '',
    show_scarcity: false,
    reveal_location_after_claim: false,
    reward_points: 10,
    is_sponsored: false,
    sponsor_name: '',
    rarity: 'standard',
    status: 'active',
    is_public: true,
    is_active: true,
    // Trigger settings
    trigger_type: 'none', // none, secret_code, url_visit, embed, webhook
    trigger_secret_code: '',
    trigger_url: '',
    embed_allowed_domains: '',
  });
  
  const [showEmbedCode, setShowEmbedCode] = useState(false);

  // Load badge data
  useEffect(() => {
    const loadBadge = async () => {
      try {
        const res = await api.get(`/admin/badges/${badgeId}`);
        const badge = res.data.badge;
        
        setFormData({
          ...badge,
          target_latitude: badge.target_latitude || '',
          target_longitude: badge.target_longitude || '',
          geofence_radius_meters: badge.geofence_radius_meters || 50,
          required_posts_count: badge.required_posts_count || '',
          required_login_days: badge.required_login_days || '',
          required_comments_count: badge.required_comments_count || '',
          required_votes_count: badge.required_votes_count || '',
          max_supply: badge.max_supply || '',
          reward_points: badge.reward_points || 10,
          trigger_type: badge.trigger_type || 'none',
          trigger_secret_code: badge.trigger_secret_code || '',
          trigger_url: badge.trigger_url || '',
          embed_allowed_domains: badge.embed_allowed_domains || '',
          required_badge_ids: badge.required_badge_ids || [],
        });
        // Load all badges for collection requirements
        try {
          const abRes = await api.get('/admin/badges');
          setAllBadges((abRes.data?.badges || []).filter(b => !b.deleted_at && b.id !== badgeId));
        } catch {}
      } catch (error) {
        console.error('Error loading badge:', error);
        toast.error('Failed to load badge');
        navigate('/admin?tab=badges');
      } finally {
        setLoading(false);
      }
    };
    
    loadBadge();
  }, [badgeId, navigate]);

  if (!isAdmin()) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-headline text-2xl text-white mb-2">Access Denied</h2>
            <p className="font-mono text-sm text-gray-500">Admin access required</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        target_latitude: formData.target_latitude ? parseFloat(formData.target_latitude) : null,
        target_longitude: formData.target_longitude ? parseFloat(formData.target_longitude) : null,
        geofence_radius_meters: parseInt(formData.geofence_radius_meters),
        max_supply: formData.max_supply ? parseInt(formData.max_supply) : null,
        reward_points: parseInt(formData.reward_points),
        required_posts_count: formData.required_posts_count ? parseInt(formData.required_posts_count) : null,
        required_login_days: formData.required_login_days ? parseInt(formData.required_login_days) : null,
        required_comments_count: formData.required_comments_count ? parseInt(formData.required_comments_count) : null,
        required_votes_count: formData.required_votes_count ? parseInt(formData.required_votes_count) : null,
      };
      
      await api.put(`/admin/badges/${badgeId}`, payload);
      
      toast.success(`Badge "${formData.name}" updated successfully!`);
      navigate('/admin?tab=badges');
    } catch (error) {
      console.error('Error updating badge:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update badge';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const generateEmbedCode = () => {
    const baseUrl = window.location.origin;
    return `<!-- CYQR Badge Embed - ${formData.name} -->
<script>
(function() {
  var badge = "${formData.code}";
  var apiUrl = "${baseUrl}/api/badges/embed-claim";
  
  // Get user token from localStorage if available
  var token = localStorage.getItem('cyqr_token');
  
  if (token) {
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ badge_code: badge, source: window.location.href })
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        console.log('CYQR Badge earned:', data.badge.name);
      }
    })
    .catch(e => console.log('CYQR: User not logged in or badge already earned'));
  }
})();
</script>`;
  };

  const generateIframeEmbed = () => {
    const baseUrl = window.location.origin;
    return `<iframe 
  src="${baseUrl}/embed/badge/${formData.code}" 
  width="300" 
  height="100" 
  frameborder="0" 
  style="border-radius: 8px; background: #111;">
</iframe>`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A52A]"></div>
        </div>
      </Layout>
    );
  }

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/png', 'image/gif', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PNG, GIF, JPG, WebP, SVG allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/badges/upload-icon', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data?.icon_url || res.data?.url;
      if (!url) throw new Error('No URL returned');
      setFormData(prev => ({ ...prev, icon_url: url }));
      toast.success('Icon uploaded — click Save to apply');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Award className="w-8 h-8 text-[#C5A52A]" />
              <h1 className="font-headline text-4xl text-white">EDIT BADGE</h1>
            </div>
            <p className="font-mono text-sm text-gray-500">
              Editing: {formData.name} ({formData.code})
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Basic Info */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <h3 className="font-headline text-xl text-white mb-4">BASIC INFO</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Code
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  rows={3}
                />
              </div>

              <div className="mt-4">
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Icon (emoji, URL, or upload a file)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.icon_url}
                    onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                    className="flex-1 px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="Paste emoji, image URL, or click Upload ↓"
                  />
                  {/* Upload button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/gif,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleIconUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#1a1a1a] border border-[#C5A52A]/40 text-[#C5A52A] hover:bg-[#C5A52A]/10 transition-colors font-mono text-xs disabled:opacity-50"
                  >
                    {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {/* Preview */}
                  <div className="w-14 h-12 bg-[#111] border border-[#2a2a2a] rounded flex items-center justify-center flex-shrink-0">
                    {formData.icon_url?.startsWith('http') ? (
                      <img src={formData.icon_url} alt="" className="w-10 h-10 object-contain" onError={e => { e.target.style.opacity = 0.3; }} />
                    ) : (
                      <span className="text-2xl">{formData.icon_url}</span>
                    )}
                  </div>
                </div>
                <p className="font-mono text-[10px] text-gray-600 mt-1">
                  Accepted: PNG, GIF, JPG, WebP, SVG — upload replaces the current icon. Click Save to apply.
                </p>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Type
                  </label>
                  <select
                    value={formData.badge_type}
                    onChange={(e) => setFormData({ ...formData, badge_type: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="location">Location</option>
                    <option value="activity">Activity</option>
                    <option value="collection">Collection</option>
                    <option value="secret">Secret/Hidden</option>
                    <option value="trigger">External Trigger</option>
                  </select>
                </div>

                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>

                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Rarity
                  </label>
                  <select
                    value={formData.rarity}
                    onChange={(e) => setFormData({ ...formData, rarity: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="standard">Standard</option>
                    <option value="special">Special</option>
                    <option value="official">Official</option>
                    <option value="legacy">Legacy</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Trigger Settings - For external/embed badges */}
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-purple-400" />
                <h3 className="font-headline text-xl text-white">TRIGGER SETTINGS</h3>
              </div>
              
              <p className="font-mono text-xs text-purple-300 mb-4">
                Configure how this badge can be earned through external triggers (for sponsors, event tickets, etc.)
              </p>

              <div className="mb-4">
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Trigger Type
                </label>
                <select
                  value={formData.trigger_type}
                  onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111] border border-purple-500/30 text-white font-mono text-sm focus:border-purple-500 focus:outline-none"
                >
                  <option value="none">None (Manual/Location only)</option>
                  <option value="secret_code">Secret Code (user enters code)</option>
                  <option value="embed">Embed Script (3rd party sites)</option>
                </select>
              </div>

              {formData.trigger_type === 'secret_code' && (
                <div className="mt-4">
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Secret Code
                  </label>
                  <input
                    type="text"
                    value={formData.trigger_secret_code}
                    onChange={(e) => setFormData({ ...formData, trigger_secret_code: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-purple-500/30 text-white font-mono text-sm focus:border-purple-500 focus:outline-none"
                    placeholder="e.g., SUMMER2026"
                  />
                  <p className="font-mono text-xs text-purple-400 mt-2">
                    Users can claim this badge at /badges by entering this code
                  </p>
                </div>
              )}

              {formData.trigger_type === 'embed' && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Allowed Domains (comma separated, or * for all)
                    </label>
                    <input
                      type="text"
                      value={formData.embed_allowed_domains}
                      onChange={(e) => setFormData({ ...formData, embed_allowed_domains: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-purple-500/30 text-white font-mono text-sm focus:border-purple-500 focus:outline-none"
                      placeholder="ticketmaster.ca, eventbrite.com"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowEmbedCode(!showEmbedCode)}
                    className="flex items-center gap-2 text-purple-400 hover:text-purple-300 font-mono text-sm"
                  >
                    <Code className="w-4 h-4" />
                    {showEmbedCode ? 'Hide' : 'Show'} Embed Code
                  </button>

                  {showEmbedCode && (
                    <div className="space-y-4">
                      <div className="bg-[#0a0a0a] border border-purple-500/20 rounded p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-mono text-xs text-purple-400">JavaScript Embed</span>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(generateEmbedCode());
                              toast.success('Embed code copied!');
                            }}
                            className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                        <pre className="font-mono text-xs text-gray-400 whitespace-pre-wrap overflow-x-auto">
                          {generateEmbedCode()}
                        </pre>
                      </div>

                      <div className="bg-[#0a0a0a] border border-purple-500/20 rounded p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-mono text-xs text-purple-400">iFrame Embed (Visual)</span>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(generateIframeEmbed());
                              toast.success('iFrame code copied!');
                            }}
                            className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                        <pre className="font-mono text-xs text-gray-400 whitespace-pre-wrap">
                          {generateIframeEmbed()}
                        </pre>
                      </div>

                      <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3">
                        <p className="font-mono text-xs text-blue-400">
                          <strong>Instructions for 3rd parties:</strong><br/>
                          1. Add this code to their success/confirmation page<br/>
                          2. When a logged-in CYQR user loads their page, they automatically earn this badge<br/>
                          3. Works for: ticket purchase confirmations, event check-ins, contest entries, etc.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Supply & Points */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <h3 className="font-headline text-xl text-white mb-4">SUPPLY & REWARDS</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Max Supply (empty = unlimited)
                  </label>
                  <input
                    type="number"
                    value={formData.max_supply}
                    onChange={(e) => setFormData({ ...formData, max_supply: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="Unlimited"
                  />
                </div>

                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Reward Points
                  </label>
                  <input
                    type="number"
                    value={formData.reward_points}
                    onChange={(e) => setFormData({ ...formData, reward_points: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_scarcity}
                    onChange={(e) => setFormData({ ...formData, show_scarcity: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="font-mono text-sm text-white">Show scarcity (X/Y remaining)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_sponsored}
                    onChange={(e) => setFormData({ ...formData, is_sponsored: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="font-mono text-sm text-white">Sponsored badge</span>
                </label>
              </div>

              {formData.is_sponsored && (
                <div className="mt-4">
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Sponsor Name
                  </label>
                  <input
                    type="text"
                    value={formData.sponsor_name || ''}
                    onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="Business Name"
                  />
                </div>
              )}
            </div>

            {/* Location Settings */}
            {formData.badge_type === 'location' && (
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-[#C5A52A]" />
                  <h3 className="font-headline text-xl text-white">LOCATION SETTINGS</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={formData.target_latitude}
                      onChange={(e) => setFormData({ ...formData, target_latitude: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={formData.target_longitude}
                      onChange={(e) => setFormData({ ...formData, target_longitude: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Radius (meters)
                    </label>
                    <input
                      type="number"
                      value={formData.geofence_radius_meters}
                      onChange={(e) => setFormData({ ...formData, geofence_radius_meters: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Venue Name
                    </label>
                    <input
                      type="text"
                      value={formData.venue_name || ''}
                      onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Venue Category
                    </label>
                    <input
                      type="text"
                      value={formData.venue_category || ''}
                      onChange={(e) => setFormData({ ...formData, venue_category: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Collection Badge Requirements */}
            {formData.badge_type === 'collection' && (
              <div className="bg-blue-900/10 border border-blue-500/30 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="w-5 h-5 text-blue-400" />
                  <h3 className="font-headline text-xl text-white">REQUIRED BADGES</h3>
                </div>
                <p className="font-mono text-xs text-blue-300 mb-4">
                  Select which badges a user must earn before this collection badge is unlocked.
                </p>
                {allBadges.filter(b => b.badge_type !== 'collection').length === 0 ? (
                  <p className="text-gray-500 text-sm font-mono">No badges available. Create some location or activity badges first.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {allBadges.filter(b => b.badge_type !== 'collection').map(b => (
                      <label key={b.id} className="flex items-center gap-3 cursor-pointer bg-[#0a0a0a] border border-[#1a1a1a] rounded px-3 py-2 hover:border-blue-500/40 transition-colors">
                        <input
                          type="checkbox"
                          checked={(formData.required_badge_ids || []).includes(b.id)}
                          onChange={e => {
                            const ids = formData.required_badge_ids || [];
                            setFormData({
                              ...formData,
                              required_badge_ids: e.target.checked
                                ? [...ids, b.id]
                                : ids.filter(id => id !== b.id)
                            });
                          }}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-lg">{b.icon_url?.startsWith('http') ? '' : (b.icon_url || '🏅')}</span>
                        <div className="min-w-0">
                          <div className="text-sm text-white font-medium truncate">{b.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{b.code} · {b.badge_type}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {(formData.required_badge_ids || []).length > 0 && (
                  <p className="mt-3 text-sm text-blue-300 font-mono">
                    ✓ {(formData.required_badge_ids || []).length} badge{(formData.required_badge_ids || []).length !== 1 ? 's' : ''} required
                  </p>
                )}
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                className={`flex-1 py-4 px-6 rounded font-headline text-lg transition-all ${
                  saving
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-[#C5A52A] hover:bg-[#D4B93A] text-black'
                }`}
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Save className="w-5 h-5" />
                    SAVE CHANGES
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/admin?tab=badges')}
                className="px-6 py-4 rounded border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-gray-500 transition-colors font-mono text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminBadgeEditor;
